import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { MenRoomLockerRow } from './LockersMenRoomLayout';

/**
 * Women’s room schematic: top wall 1–8, right wall 9–25, bottom-left 26–27.
 * Other slotLabel values use the table only.
 */
const TILE = 40;

/** Top wall, left → right */
const TOP_SLOTS = Array.from({ length: 8 }, (_, i) => String(i + 1));
/** Right wall, top → bottom (below top row) */
const RIGHT_SLOTS = Array.from({ length: 17 }, (_, i) => String(i + 9));
/** Bottom wall, left side */
const BOTTOM_LEFT_SLOTS = ['26', '27'];

export function normalizeWomenRoomSlotKey(raw: string): string | null {
  const s = raw.trim();
  if (!s) return null;
  if (!/^0*\d+$/.test(s)) return null;
  const n = parseInt(s, 10);
  if (Number.isNaN(n) || n < 1 || n > 27) return null;
  return String(n);
}

function formatUser(u: NonNullable<MenRoomLockerRow['assignee']>) {
  return `${u.firstName} ${u.lastName}${u.department ? ` (${u.department})` : ''}`;
}

export interface LockersWomenRoomLayoutProps {
  lockersByLabel: Map<string, MenRoomLockerRow>;
  canManage: boolean;
  onSelectSlot: (row: MenRoomLockerRow | null, canonicalLabel: string) => void;
}

const LockersWomenRoomLayout: React.FC<LockersWomenRoomLayoutProps> = ({
  lockersByLabel,
  canManage,
  onSelectSlot
}) => {
  const { t } = useTranslation();

  const renderTile = (canonicalLabel: string) => {
    const row = lockersByLabel.get(canonicalLabel) ?? null;
    const hasRecord = !!row;
    const occupied = !!(row && (row.userId || (row.externalOccupantLabel && row.externalOccupantLabel.trim())));

    let bg: string;
    let borderColor: string;
    if (!hasRecord) {
      bg = 'action.hover';
      borderColor = 'divider';
    } else if (occupied) {
      bg = 'warning.light';
      borderColor = 'warning.main';
    } else {
      bg = 'success.light';
      borderColor = 'success.main';
    }

    const titleParts = [`#${canonicalLabel}`];
    if (!hasRecord) {
      titleParts.push(
        t(
          'lockers.layoutNotConfigured',
          'Not in the system yet — click to create this locker and optionally assign someone'
        )
      );
    } else if (occupied) {
      if (row.assignee) titleParts.push(formatUser(row.assignee));
      else if (row.externalOccupantLabel?.trim()) titleParts.push(row.externalOccupantLabel.trim());
      else titleParts.push(t('lockers.free', 'Free'));
    } else {
      titleParts.push(t('lockers.free', 'Free'));
    }

    const clickable = hasRecord || canManage;
    const title = titleParts.join(' — ');

    return (
      <Tooltip title={title}>
        <Box
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          onClick={() => clickable && onSelectSlot(row, canonicalLabel)}
          onKeyDown={(e) => {
            if (!clickable) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectSlot(row, canonicalLabel);
            }
          }}
          sx={{
            width: TILE,
            height: TILE,
            boxSizing: 'border-box',
            bgcolor: bg,
            border: '2px solid',
            borderColor,
            borderRadius: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.72rem',
            fontWeight: 600,
            cursor: clickable ? 'pointer' : 'default',
            '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 }
          }}
        >
          {canonicalLabel}
        </Box>
      </Tooltip>
    );
  };

  const cols = `repeat(9, ${TILE}px)`;
  const rows = `repeat(19, ${TILE}px)`;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        {t('lockers.roomLayoutTitleWomen', 'Room layout (women)')}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: cols,
            gridTemplateRows: rows,
            gap: 0.5,
            p: 1.5,
            border: '3px solid',
            borderColor: 'text.primary',
            borderRadius: 1,
            width: 'fit-content'
          }}
        >
          {/* Top wall 1–8 */}
          {TOP_SLOTS.map((label, i) => (
            <Box key={`top-${label}`} sx={{ gridColumn: i + 1, gridRow: 1, display: 'flex', justifyContent: 'center' }}>
              {renderTile(label)}
            </Box>
          ))}

          {/* Top-right corner (empty) */}
          <Box sx={{ gridColumn: 9, gridRow: 1, width: TILE, height: TILE }} />

          {/* Main floor — keeps grid spacing only (no fill) */}
          <Box
            sx={{
              gridColumn: '1 / span 8',
              gridRow: '2 / span 17',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 17 * TILE,
              minWidth: 8 * TILE,
              pointerEvents: 'none'
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t('lockers.roomFloor', 'Room')}
            </Typography>
          </Box>

          {/* Right wall 9–25 */}
          {RIGHT_SLOTS.map((label, i) => (
            <Box
              key={`right-${label}`}
              sx={{ gridColumn: 9, gridRow: i + 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {renderTile(label)}
            </Box>
          ))}

          {/* Bottom-left 26–27 */}
          <Box sx={{ gridColumn: 1, gridRow: 19, display: 'flex', justifyContent: 'center' }}>
            {renderTile(BOTTOM_LEFT_SLOTS[0])}
          </Box>
          <Box sx={{ gridColumn: 2, gridRow: 19, display: 'flex', justifyContent: 'center' }}>
            {renderTile(BOTTOM_LEFT_SLOTS[1])}
          </Box>

          {/* Bottom strip — grid spacer only (no fill) */}
          <Box
            sx={{
              gridColumn: '3 / span 6',
              gridRow: 19,
              minHeight: TILE,
              minWidth: 6 * TILE,
              pointerEvents: 'none'
            }}
          />

          {/* Bottom-right corner under locker 25 */}
          <Box sx={{ gridColumn: 9, gridRow: 19, width: TILE, height: TILE }} />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                bgcolor: 'success.light',
                border: '2px solid',
                borderColor: 'success.main',
                borderRadius: 0.5
              }}
            />
            <Typography variant="caption">{t('lockers.legendFree', 'Free locker')}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                bgcolor: 'warning.light',
                border: '2px solid',
                borderColor: 'warning.main',
                borderRadius: 0.5
              }}
            />
            <Typography variant="caption">{t('lockers.legendTaken', 'Taken locker')}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LockersWomenRoomLayout;
